import React, { useEffect, useState, useCallback } from "react";
import "./CryptoApiOptionChain.css";
import { environment } from "environment/environment";
import usePageTitle from "component/common/PageTitle";
import useCryptoApiWebsocket from "service/websocket/CryptoApiWebsocket";

export default function CryptoApiOptionChain() {
    // WebSocket connection
    const { cryptoApiData } = useCryptoApiWebsocket(environment.cryptoApiWSUrl);
    const [cryptoOptionDataModelList, setCryptoOptionDataModelList] = useState([]);
    const [selectedStrikeFilter, setSelectedStrikeFilter] = useState("10");
    const [aboveSpotIndex, setAboveSpotIndex] = useState(null);
    const [belowSpotIndex, setBelowSpotIndex] = useState(null);
    const [indexFilterValueChanges, setIndexFilterValueChangeFilter] = useState("BTC");
    // =======================
    // Stats State
    // =======================
    const [stats, setStats] = useState({
        spotPrice: null,
        highestCallVolume: null,
        secondHighestCallVolume: null,
        highestPutVolume: null,
        secondHighestPutVolume: null,
        highestCallOi: null,
        secondHighestCallOi: null,
        highestPutOi: null,
        secondHighestPutOi: null,
        highestCallOiChg: null,
        secondHighestCallOiChg: null,
        highestPutOiChg: null,
        secondHighestPutOiChg: null,
        highestCall3MOIChange: null,
        secondHighestCall3MOIChange: null,
        highestPut3MOIChange: null,
        secondHighestPut3MOIChange: null,
        highestCallTikVolumeChange: null,
        secondHighestCallTikVolumeChange: null,
        highestPutTikVolumeChange: null,
        secondHighestPutTikVolumeChange: null,
        highestCall3MVolumeChange: null,
        secondHighestCall3MVolumeChange: null,
        highestPut3MVolumeChange: null,
        secondHighestPut3MVolumeChange: null,
        highestCall3MOICHGChange: null,
        secondHighestCall3MOICHGChange: null,
        highestPut3MOICHGChange: null,
        secondHighestPut3MOICHGChange: null,

        highestCallVolStrike: null,
        secondHighestCallVolStrike: null,
        highestCallOiStrike: null,
        secondHighestCallOiStrike: null,
        highestCallOiChgStrike: null,
        secondHighestCallOiChgStrike: null,
        highestPutVolStrike: null,
        secondHighestPutVolStrike: null,
        highestPutOiStrike: null,
        secondHighestPutOiStrike: null,
        highestPutOiChgStrike: null,
        secondHighestPutOiChgStrike: null,

        callResistance: null,
        callResistanceWttAndWtb: null,
        callResistanceOIWttAndWtb: null,
        callResistanceOIChgWttAndWtb: null,
        callResistanceOIChgWttAndWtb: null,

        putSupport: null,
        putSupportWttAndWtb: null,
        putSupportOIWttAndWtb: null,
        putSupportOIChgWttAndWtb: null,

        callTotalVolumeSum: null,
        callTotalOISum: null,
        callTotalOICHGSum: null,

        putTotalVolumeSum: null,
        putTotalOISum: null,
        putTotalOICHGSum: null,
    });

    // Percentage Helpers
    // =======================
    const getPercentage = (value, highest) => highest ? ((value / highest) * 100).toFixed(2) : "0";

    // =======================

    // Resistance Logic
    // =======================
    const getCallResistanceLogic = useCallback((highestCallVolStrike, highestCallOiStrike, spotPrice) => {
        if (highestCallVolStrike == null || highestCallOiStrike == null) return null;

        if (!highestCallVolStrike || !highestCallOiStrike) return null;

        if (highestCallVolStrike === highestCallOiStrike) return highestCallVolStrike;

        const volDiff = Math.abs(highestCallVolStrike - spotPrice);
        const oiDiff = Math.abs(highestCallOiStrike - spotPrice);
        return volDiff < oiDiff ? highestCallVolStrike : highestCallOiStrike;
    }, []);

    const getCallResistanceWttAndWtb = useCallback((callResistance, secondHighestCallVolStrike, secondHighestCallVolume, highestCallVolume) => {
        if (!callResistance || !secondHighestCallVolStrike || !secondHighestCallVolume) return "N/A";

        const percentage = getPercentage(secondHighestCallVolume, highestCallVolume);
        const per = parseFloat(percentage);

        if (secondHighestCallVolStrike < callResistance) return per >= 75 ? `WTB - ${percentage}%` : "STRNG";
        return per >= 75 ? `WTT - ${percentage}%` : "STRNG";
    }, []);

    // React-compatible version
    const getCallResistanceOIWttAndWtb = useCallback((secondHighestCallOIStrike, secondHighestCallOI, highestCallOIStrike) => {
        let callResistanceOIWttAndWtb = "";
        if (highestCallOIStrike != null && secondHighestCallOIStrike != null && secondHighestCallOIStrike < highestCallOIStrike) {
            const percentage = getCallOiPercentage(secondHighestCallOI);
            const per = parseInt(percentage);
            if (per >= 75) {
                callResistanceOIWttAndWtb = `WTB - ${percentage}`;
                // console.log("Resistance OI is =>", callResistanceOIWttAndWtb);
            } else {
                callResistanceOIWttAndWtb = `STRNG`;
                // console.log("Resistance OI is =>", callResistanceOIWttAndWtb);
            }
        } else {
            const percentage = getCallOiPercentage(secondHighestCallOI);
            const per = parseInt(percentage);
            if (per >= 75) {
                callResistanceOIWttAndWtb = `WTT - ${percentage}`;
            } else {
                callResistanceOIWttAndWtb = `STRNG`;
            }
            // console.log("Resistance OI is =>", callResistanceOIWttAndWtb);
        }

        return callResistanceOIWttAndWtb;
    }, []);

    // React-compatible version
    const getCallResistanceOIChgWttAndWtb = useCallback((secondHighestCallOIChgStrike, secondHighestCallOIChg, highestOIChgStrike) => {
        let callResistanceOIChgWttAndWtb = "";
        if (highestOIChgStrike != null && secondHighestCallOIChgStrike != null && secondHighestCallOIChgStrike < highestOIChgStrike) {
            const percentage = getCallOiChgPercentage(secondHighestCallOIChg);
            const per = parseInt(percentage);
            if (per >= 75) {
                callResistanceOIChgWttAndWtb = `WTB - ${percentage}`;
                // console.log("Resistance OICHG is =>", callResistanceOIChgWttAndWtb);
            } else {
                callResistanceOIChgWttAndWtb = `STRNG`;
                // console.log("Resistance OICHG is =>", callResistanceOIChgWttAndWtb);
            }
        } else {
            const percentage = getCallOiChgPercentage(secondHighestCallOIChg);
            const per = parseInt(percentage);
            if (per >= 75) {
                callResistanceOIChgWttAndWtb = `WTT - ${percentage}`;
            } else {
                callResistanceOIChgWttAndWtb = `STRNG`;
            }
            // console.log("Resistance OICHG is =>", callResistanceOIChgWttAndWtb);
        }

        return callResistanceOIChgWttAndWtb;
    }, []);

    // React-compatible version
    const getPutSupportLogic = useCallback((highestPutVolStrike, highestPutOiStrike, spotPrice) => {
        let putSupport = null;
        // 🧱 Support Logic
        if (highestPutVolStrike != null && highestPutOiStrike != null) {
            if (highestPutVolStrike === highestPutOiStrike) {
                putSupport = highestPutVolStrike;
            } else {
                const volDiff = Math.abs(highestPutVolStrike - spotPrice);
                const oiDiff = Math.abs(highestPutOiStrike - spotPrice);
                putSupport = volDiff < oiDiff ? highestPutVolStrike : highestPutOiStrike;
            }
            // console.log("putSupport =>", putSupport);
        }

        return putSupport;
    }, []);

    // React-compatible version
    const getPutSupportWttAndWtb = useCallback((secondHighestPutVolStrike, secondHighestPutVolume, putSupport) => {
        let putSupportWttAndWtb = "";
        if (putSupport != null && secondHighestPutVolStrike != null && secondHighestPutVolStrike < putSupport) {
            const percentage = getPutVolumePercentage(secondHighestPutVolume);
            const per = parseInt(percentage);
            if (per >= 75) {
                putSupportWttAndWtb = `WTB - ${percentage}`;
                //  console.log("Support is =>", putSupportWttAndWtb);
            } else {
                putSupportWttAndWtb = `STRNG`;
                //   console.log("Support is =>", putSupportWttAndWtb);
            }
        } else {
            const percentage = getPutVolumePercentage(secondHighestPutVolume);
            const per = parseInt(percentage);
            if (per >= 75) {
                putSupportWttAndWtb = `WTT - ${percentage}`;
            } else {
                putSupportWttAndWtb = `STRNG`;
            }
            //console.log("Support is =>", putSupportWttAndWtb);
        }
        return putSupportWttAndWtb;
    }, []);

    // ✅ React-compatible version
    const getPutSupportOIWttAndWtb = useCallback((secondHighestPutOIStrike, secondHighestPutOI, highestPutOIStrike) => {
        let putSupportOIWttAndWtb = "";
        if (highestPutOIStrike != null && secondHighestPutOIStrike != null && secondHighestPutOIStrike < highestPutOIStrike) {
            const percentage = getPutOiPercentage(secondHighestPutOI);
            const per = parseInt(percentage);
            if (per >= 75) {
                putSupportOIWttAndWtb = `WTB - ${percentage}`;
                // console.log("Support OI is =>", putSupportOIWttAndWtb);
            } else {
                putSupportOIWttAndWtb = `STRNG`;
                // console.log("Support OI is =>", putSupportOIWttAndWtb);
            }
        } else {
            const percentage = getPutOiPercentage(secondHighestPutOI);
            const per = parseInt(percentage);
            if (per >= 75) {
                putSupportOIWttAndWtb = `WTT - ${percentage}`;
            } else {
                putSupportOIWttAndWtb = `STRNG`;
            }
            // console.log("Support OI is =>", putSupportOIWttAndWtb);
        }
        return putSupportOIWttAndWtb;
    }, []);

    // ✅ React-compatible version
    const getPutSupportOIChgWttAndWtb = useCallback((secondHighestPutOIChgStrike, secondHighestPutOIChg, highestPutOIChgStrike) => {
        let putSupportOIChgWttAndWtb = "";
        if (highestPutOIChgStrike && secondHighestPutOIChgStrike != null && secondHighestPutOIChgStrike < highestPutOIChgStrike) {
            const percentage = getPutOiChgPercentage(secondHighestPutOIChg);
            const per = parseInt(percentage);
            if (per >= 75) {
                putSupportOIChgWttAndWtb = `WTB - ${percentage}`;
                // console.log("Support OICHG is =>", putSupportOIChgWttAndWtb);
            } else {
                putSupportOIChgWttAndWtb = `STRNG`;
                // console.log("Support OICHG is =>", putSupportOIChgWttAndWtb);
            }
        } else {
            const percentage = getPutOiChgPercentage(secondHighestPutOIChg);
            const per = parseInt(percentage);
            if (per >= 75) {
                putSupportOIChgWttAndWtb = `WTT - ${percentage}`;
            } else {
                putSupportOIChgWttAndWtb = `STRNG`;
            }
            // console.log("Support OICHG is =>", putSupportOIChgWttAndWtb);
        }
        return putSupportOIChgWttAndWtb;
    }, []);

    // Main Update Function
    // =======================
    const updateTopCallVolumes = useCallback(
        (cryptoApiOptionData = []) => {
            if (!Array.isArray(cryptoApiOptionData)) return;

            const sortDesc = (arr) => [...new Set(arr)].sort((a, b) => b - a);
            const extract = (key) => cryptoApiOptionData.map((row) => row[key]).filter((v) => v != null);
            const findStrike = (key, value) => cryptoApiOptionData.find((row) => row[key] === value)?.strikePrice ?? null;

            const spotPrice = cryptoApiOptionData[0]?.spotPrice || 0;

            const sortedCallVol = sortDesc(extract("ceVolume"));
            const sortedPutVol = sortDesc(extract("peVolume"));
            const sortedCallOi = sortDesc(extract("ceOI"));
            const sortedPutOi = sortDesc(extract("peOI"));
            const sortedCallOiChg = sortDesc(extract("ceOIChg"));
            const sortedPutOiChg = sortDesc(extract("peOIChg"));
            const sortedCall3MOIChange = sortDesc(extract("ce3MOIChange"));
            const sortedPut3MOIChange = sortDesc(extract("pe3MOIChange"));
            const sortedCallTikVolChange = sortDesc(extract("ceTikByTikVolumeChange"));
            const sortedPutTikVolChange = sortDesc(extract("peTikByTikVolumeChange"));
            const sortedCall3MVolumeChange = sortDesc(extract("ce3MVolumeChange"));
            const sortedPut3MVolumeChange = sortDesc(extract("pe3MVolumeChange"));
            const sortedCall3MOIChgChange = sortDesc(extract("ce3MOICHGChange"));
            const sortedPut3MOIChgChange = sortDesc(extract("pe3MOICHGChange"));

            const updatedStats = {
                spotPrice,
                highestCallVolume: sortedCallVol[0] ?? null,
                secondHighestCallVolume: sortedCallVol[1] ?? null,
                highestPutVolume: sortedPutVol[0] ?? null,
                secondHighestPutVolume: sortedPutVol[1] ?? null,
                highestCallOi: sortedCallOi[0] ?? null,
                secondHighestCallOi: sortedCallOi[1] ?? null,
                highestPutOi: sortedPutOi[0] ?? null,
                secondHighestPutOi: sortedPutOi[1] ?? null,
                highestCallOiChg: sortedCallOiChg[0] ?? null,
                secondHighestCallOiChg: sortedCallOiChg[1] ?? null,
                highestPutOiChg: sortedPutOiChg[0] ?? null,
                secondHighestPutOiChg: sortedPutOiChg[1] ?? null,
                highestCall3MOIChange: sortedCall3MOIChange[0] ?? null,
                secondHighestCall3MOIChange: sortedCall3MOIChange[1] ?? null,
                highestPut3MOIChange: sortedPut3MOIChange[0] ?? null,
                secondHighestPut3MOIChange: sortedPut3MOIChange[1] ?? null,
                highestCallTikVolumeChange: sortedCallTikVolChange[0] ?? null,
                secondHighestCallTikVolumeChange: sortedCallTikVolChange[1] ?? null,
                highestPutTikVolumeChange: sortedPutTikVolChange[0] ?? null,
                secondHighestPutTikVolumeChange: sortedPutTikVolChange[1] ?? null,
                highestCall3MVolumeChange: sortedCall3MVolumeChange[0] ?? null,
                secondHighestCall3MVolumeChange: sortedCall3MVolumeChange[1] ?? null,
                highestPut3MVolumeChange: sortedPut3MVolumeChange[0] ?? null,
                secondHighestPut3MVolumeChange: sortedPut3MVolumeChange[1] ?? null,
                highestCall3MOICHGChange: sortedCall3MOIChgChange[0] ?? null,
                secondHighestCall3MOICHGChange: sortedCall3MOIChgChange[1] ?? null,
                highestPut3MOICHGChange: sortedPut3MOIChgChange[0] ?? null,
                secondHighestPut3MOICHGChange: sortedPut3MOIChgChange[1] ?? null,

                highestCallVolStrike: findStrike("ceVolume", sortedCallVol[0]),
                secondHighestCallVolStrike: findStrike("ceVolume", sortedCallVol[1]),
                highestCallOiStrike: findStrike("ceOI", sortedCallOi[0]),
                secondHighestCallOiStrike: findStrike("ceOI", sortedCallOi[1]),
                highestCallOiChgStrike: findStrike("ceOIChg", sortedCallOiChg[0]),
                secondHighestCallOiChgStrike: findStrike("ceOIChg", sortedCallOiChg[1]),

                highestPutVolStrike: findStrike("peVolume", sortedPutVol[0]),
                secondHighestPutVolStrike: findStrike("peVolume", sortedPutVol[1]),
                highestPutOiStrike: findStrike("peOI", sortedPutOi[0]),
                secondHighestPutOiStrike: findStrike("peOI", sortedPutOi[1]),
                highestPutOiChgStrike: findStrike("peOIChg", sortedPutOiChg[0]),
                secondHighestPutOiChgStrike: findStrike("peOIChg", sortedPutOiChg[1]),

                callTotalVolumeSum: sortedCallVol.reduce((sum, val) => sum + val, 0),
                callTotalOISum: sortedCallOi.reduce((sum, val) => sum + val, 0),
                callTotalOICHGSum: sortedCallOiChg.reduce((sum, val) => sum + val, 0),

                putTotalVolumeSum: sortedPutVol.reduce((sum, val) => sum + val, 0),
                putTotalOISum: sortedPutOi.reduce((sum, val) => sum + val, 0),
                putTotalOICHGSum: sortedPutOiChg.reduce((sum, val) => sum + val, 0),

            };

            // Derived logic
            updatedStats.callResistance = getCallResistanceLogic(updatedStats.highestCallVolStrike, updatedStats.highestCallOiStrike, spotPrice);
            updatedStats.callResistanceWttAndWtb = getCallResistanceWttAndWtb(updatedStats.callResistance, updatedStats.secondHighestCallVolStrike, updatedStats.secondHighestCallVolume, updatedStats.highestCallVolume);
            updatedStats.callResistanceOIWttAndWtb = getCallResistanceOIWttAndWtb(updatedStats.secondHighestCallOiStrike, updatedStats.secondHighestCallOi, updatedStats.highestCallOiStrike);
            updatedStats.callResistanceOIChgWttAndWtb = getCallResistanceOIChgWttAndWtb(updatedStats.secondHighestCallOiChgStrike, updatedStats.secondHighestCallOiChg, updatedStats.highestCallOiChgStrike);

            updatedStats.putSupport = getPutSupportLogic(updatedStats.highestPutVolStrike, updatedStats.highestPutOiStrike, spotPrice);
            updatedStats.putSupportWttAndWtb = getPutSupportWttAndWtb(updatedStats.secondHighestPutVolStrike, updatedStats.secondHighestPutVolume, updatedStats.putSupport);
            updatedStats.putSupportOIWttAndWtb = getPutSupportOIWttAndWtb(updatedStats.secondHighestPutOiStrike, updatedStats.secondHighestPutOi, updatedStats.highestPutOiStrike);
            updatedStats.putSupportOIChgWttAndWtb = getPutSupportOIChgWttAndWtb(updatedStats.secondHighestPutOiChgStrike, updatedStats.secondHighestPutOiChg, updatedStats.highestPutOiChgStrike);

            setStats(updatedStats);
        },
        [getCallResistanceLogic, getCallResistanceWttAndWtb, getCallResistanceOIWttAndWtb, getCallResistanceOIChgWttAndWtb, getPutSupportLogic, getPutSupportWttAndWtb, getPutSupportOIWttAndWtb, getPutSupportOIChgWttAndWtb]
    );



    // -------------------
    // Fetch WebSocket Data
    // -------------------
    useEffect(() => {
        const cryptoOptionData = cryptoApiData?.[indexFilterValueChanges];
        if (!cryptoOptionData) return;

        if (Array.isArray(cryptoOptionData)) {


            const filtered = filterNiftyOptionData(cryptoOptionData, cryptoOptionData[0]?.spotPrice || 0);
            prepareStrikeMarkers(filtered, cryptoOptionData[0]?.spotPrice || 0);

            updateTopCallVolumes(filtered); // merged top volumes & OI logic

            console.log('crypto api option data => ', filtered);
            console.log('Top Call/Put stats => ', stats);
            setCryptoOptionDataModelList(filtered);
        }
    }, [cryptoApiData, selectedStrikeFilter, updateTopCallVolumes]);

    // -------------------
    // Filter Option Data
    // -------------------
    const filterNiftyOptionData = (data, spot) => {
        if (!spot || selectedStrikeFilter === "ALL") return data;
        const range = Number(selectedStrikeFilter - 1);
        const closestIndex = data.reduce((closestIdx, curr, idx) =>
            Math.abs(curr.strikePrice - spot) < Math.abs(data[closestIdx].strikePrice - spot)
                ? idx : closestIdx, 0);
        const start = Math.max(0, closestIndex - range);
        const end = Math.min(data.length - 1, closestIndex + range);
        return data.slice(start - 1, end + 1);
    };

    // -------------------
    // Prepare Strike Markers
    // -------------------
    const prepareStrikeMarkers = (list, spot) => {
        let aboveIndex = null;
        let belowIndex = null;

        if (selectedStrikeFilter === "ALL" || selectedStrikeFilter === "10") {
            for (let i = 0; i < list.length; i++) {
                const row = list[i];

                if (
                    belowIndex === null && row.strikePrice < spot && list[i + 5] &&
                    list[i + 5].strikePrice >= spot
                ) {
                    belowIndex = i;
                }
                if (aboveIndex === null && row.strikePrice > spot && i >= 3 &&
                    list[i - 3].strikePrice >= spot
                ) {
                    aboveIndex = i;
                }

                if (belowIndex !== null && aboveIndex !== null) break;
            }
        }

        setBelowSpotIndex(belowIndex);
        setAboveSpotIndex(aboveIndex);
    };

    const handleStrikeFilterChange = (event) => {
        setSelectedStrikeFilter(event.target.value);
    };

    const handleIndexFilterValueChange = (event) => {
        setIndexFilterValueChangeFilter(event.target.value);
    };
    // Percentage Helper Functions
    // =======================
    const getCallVolumePercentage = (volume) =>
        stats.highestCallVolume && volume ? ((volume / stats.highestCallVolume) * 100).toFixed(2) + "%" : "0%";

    const getPutVolumePercentage = (volume) => stats.highestPutVolume && volume ? ((volume / stats.highestPutVolume) * 100).toFixed(2) + "%"
        : "0%";

    const getCallOiPercentage = (oi) => stats.highestCallOi && oi ? ((oi / stats.highestCallOi) * 100).toFixed(2) + "%"
        : "0%";

    const getPutOiPercentage = (oi) => stats.highestPutOi && oi ? ((oi / stats.highestPutOi) * 100).toFixed(2) + "%"
        : "0%";

    const getCallOiChgPercentage = (val) => stats.highestCallOiChg && val ? ((val / stats.highestCallOiChg) * 100).toFixed(2) + "%"
        : "0%";

    const getPutOiChgPercentage = (val) => stats.highestPutOiChg && val ? ((val / stats.highestPutOiChg) * 100).toFixed(2) + "%"
        : "0%";

    //highest and 2nd highest class changes dynamically

    const getCallVolumeClass = (volume) => {
        let highestVolume = stats.highestCallVolume;
        let secondHighestVolume = stats.secondHighestCallVolume;
        if (!highestVolume || !volume) return '';

        const pct = (volume / highestVolume) * 100;

        if (volume === highestVolume) return 'call-volume-blue';
        if (volume === secondHighestVolume && pct >= 75) return 'call-volume-yellow';
        if (pct >= 75 && volume < (secondHighestVolume ?? Infinity)) return 'call-volume-bold';

        return '';
    };

    // === Volume Classes ===
    const getPutVolumeClass = (volume) => {
        let highestVolume = stats.highestPutVolume;
        let secondHighestVolume = stats.secondHighestPutVolume;
        if (!highestVolume || !volume) return '';
        const pct = (volume / highestVolume) * 100;
        if (volume === highestVolume) return 'put-volume-blue';
        if (volume === secondHighestVolume && pct >= 75) return 'put-volume-yellow';
        if (pct >= 75 && volume < (secondHighestVolume ?? Infinity)) return 'put-volume-bold';
        return '';
    };

    // === Call OI Classes ===
    const getCallOiClass = (oi) => {
        let highestOI = stats.highestCallOi;
        let secondHighestOI = stats.secondHighestCallOi;
        if (!highestOI || !oi) return '';
        const pct = (oi / highestOI) * 100;
        if (oi === highestOI) return 'call-oi-blue';
        if (oi === secondHighestOI && pct >= 75) return 'call-oi-yellow';
        if (pct >= 75 && oi < (secondHighestOI ?? Infinity)) return 'call-oi-bold';
        return '';
    };

    // === Put OI Classes ===
    const getPutOiClass = (oi) => {
        let highestOI = stats.highestPutOi;
        let secondHighestOI = stats.secondHighestPutOi;
        if (!highestOI || !oi) return '';
        const pct = (oi / highestOI) * 100;
        if (oi === highestOI) return 'put-oi-blue';
        if (oi === secondHighestOI && pct >= 75) return 'put-oi-yellow';
        if (pct >= 75 && oi < (secondHighestOI ?? Infinity)) return 'put-oi-bold';
        return '';
    };

    // === Call OI Change Classes ===
    const getCallOiChgClass = (val) => {
        let highestOIChg = stats.highestCallOiChg;
        let secondHighestOIChg = stats.secondHighestCallOiChg;
        if (!highestOIChg || !val) return '';
        const pct = (val / highestOIChg) * 100;
        if (val === highestOIChg) return 'call-oiChg-blue';
        if (val === secondHighestOIChg && pct >= 75) return 'call-oiChg-yellow';
        if (pct >= 75 && val < (secondHighestOIChg ?? Infinity)) return 'call-oiChg-bold';
        return (val < 0 && val < (secondHighestOIChg ?? Infinity)) ? 'text-danger' : 'text-success';
    };

    // === Put OI Change Classes ===
    const getPutOiChgClass = (val) => {
        let highestOIChg = stats.highestPutOiChg;
        let secondHighestOIChg = stats.secondHighestPutOiChg;
        if (!highestOIChg || !val) return '';
        const pct = (val / highestOIChg) * 100;
        if (val === highestOIChg) return 'put-oiChg-blue';
        if (val === secondHighestOIChg && pct >= 75) return 'put-oiChg-yellow';
        if (pct >= 75 && val < (secondHighestOIChg ?? Infinity)) return 'put-oiChg-bold';
        return (val < 0 && val < (secondHighestOIChg ?? Infinity)) ? 'text-danger' : 'text-success';
    };

    // Header Data
    // -------------------
    const headerData = [

        {
            label1: "RES",
            value1:
                getCallResistanceLogic(stats.highestCallVolStrike, stats.highestCallOiStrike, stats.spotPrice) + ' - ' +
                getCallResistanceWttAndWtb(getCallResistanceLogic(stats.highestCallVolStrike, stats.highestCallOiStrike, stats.spotPrice),
                    stats.secondHighestCallVolStrike, stats.secondHighestCallVolume, stats.highestCallVolume), // just call function normally
            label2: "SUP",
            value2: getPutSupportLogic(stats.highestPutVolStrike, stats.highestPutOiStrike, stats.spotPrice) + ' - ' +
                getPutSupportWttAndWtb(stats.secondHighestPutVolStrike, stats.secondHighestPutVolume, getPutSupportLogic(stats.highestPutVolStrike, stats.highestPutOiStrike, stats.spotPrice))

        },
        {
            label1: "OPEN",
            value1: "--",
            label2: "CLOSE",
            value2: "--"
        },
        {
            label1: "ExpDate",
            value1: "--",
            label2: "DateTime",
            value2: "--",
        },
    ];

    // usePageTitle(indexDayRangeData?.niftySpotPrice ?? "N/A", indexDayRangeData?.expiryDate ?? "N/A");

    // -------------------
    // JSX Table Section (remains unchanged)
    // -------------------
    return (
        <div className="crypto-option-page">
            <header className="header">
                <nav className="nav-container">
                    <div className="left-nav header-flex">
                        <div className="logo-container">
                            <img
                                src="/assets/images/noptionwave-logo-1.png"
                                className="header-logo"
                                alt="noptionwave logo"
                            />
                            {/* <span className="index-name">{indexSymbolName}</span> */}
                            <select name="indexFilter" className="index-filter" value={indexFilterValueChanges} onChange={handleIndexFilterValueChange} style={{ cursor: "pointer" }}>
                                <option value="BTC">BTC</option>
                                <option value="ETH">ETH</option>
                            </select>
                        </div>
                        {headerData.map((item, idx) => (
                            <div className="header-item" key={idx}>
                                <span className="header-line">{item.label1} : {item.value1}</span>
                                <span className="header-line">{item.label2} : {item.value2}</span>
                            </div>
                        ))}
                    </div>
                </nav>
            </header>

            <div className="table-container">
                <table className="responsive-table table-bordered text-center sticky-header">
                    <thead>
                        <tr>
                            <th>VEGA</th>
                            <th>GAMA</th>
                            <th>THITA</th>
                            <th>DELTA</th>
                            <th>IV</th>
                            <th>OICHG</th>
                            <th>OI</th>
                            <th>VOLUME</th>
                            <th>LTP/REV</th>
                            <th>
                                <select name="strikeFilter" className="strike-filter" value={selectedStrikeFilter} onChange={handleStrikeFilterChange} style={{ cursor: "pointer" }}>
                                    <option value="10">ATM</option>
                                    <option value="ALL">ALL</option>
                                </select>
                            </th>
                            <th>LTP/REV</th>
                            <th>VOLUME</th>
                            <th>OI</th>
                            <th>OICHG</th>
                            <th>IV</th>
                            <th>DELTA</th>
                            <th>THITA</th>
                            <th>GAMA</th>
                            <th>VEGA</th>
                        </tr>
                    </thead>

                    <tbody>
                        {cryptoOptionDataModelList ? (
                            cryptoOptionDataModelList.map((row, idx) => {
                                const nextRow = cryptoOptionDataModelList[idx + 1];
                                const spot = cryptoOptionDataModelList[0]?.spotPrice || 0;

                                const isSpotBetween =
                                    nextRow &&
                                    spot != null &&
                                    spot > Math.min(row.strikePrice, nextRow.strikePrice) &&
                                    spot < Math.max(row.strikePrice, nextRow.strikePrice);

                                const insertAbove = idx === aboveSpotIndex;
                                const insertBelow = idx === belowSpotIndex;

                                return (
                                    <React.Fragment key={idx}>
                                        {/* Original row */}
                                        <tr>
                                            <td>
                                                {Number(row.ceVega).toFixed(2) ?? "--"}<br />
                                            </td>
                                            <td>
                                                {Number(row.ceGamma).toFixed(2) ?? "--"}<br />
                                            </td>
                                            <td>
                                                {Number(row.ceTheta).toFixed(2) ?? "--"}<br />
                                            </td>
                                            <td>
                                                {Number(row.ceDelta).toFixed(2) ?? "--"}<br />
                                            </td>
                                            <td >
                                                {Number(row.ceIV).toFixed(2) ?? "--"}
                                            </td>
                                            <td className={getCallOiChgClass(row.ceOIChg)}>
                                                {Number(row.ceOIChg).toFixed(0) ?? "--"}
                                                <br />
                                                {getCallOiChgPercentage(row.ceOIChg)}
                                            </td>
                                            <td className={getCallOiClass(row.ceOI)}>
                                                {Number(row.ceOI).toFixed(0) ?? "--"}
                                                <br />
                                                {getCallOiPercentage(row.ceOI)}
                                            </td>
                                            <td className={getCallVolumeClass(row.ceVolume)}>
                                                {row.ceVolume ?? "--"}
                                                <br />
                                                {getCallVolumePercentage(row.ceVolume)}
                                            </td>

                                            <td>{Number(row.ceLtp).toFixed(2) ?? "--"}
                                                <br />
                                                {Number(row.ceReversal).toFixed(2) ?? "--"}
                                            </td>
                                            <td className="strike-price-color">{row.strikePrice ?? "--"}</td>
                                            <td>{Number(row.peLtp).toFixed(2) ?? "--"}
                                                <br />
                                                {Number(row.peReversal).toFixed(2) ?? "--"}
                                            </td>

                                            <td className={getPutVolumeClass(row.peVolume)}>
                                                {row.peVolume ?? "--"}
                                                <br />
                                                {getPutVolumePercentage(row.peVolume)}
                                            </td>
                                            <td className={getPutOiClass(row.peOI)}>
                                                {Number(row.peOI).toFixed(0) ?? "--"}
                                                <br />
                                                {getPutOiPercentage(row.peOI)}
                                            </td>
                                            <td className={getPutOiChgClass(row.peOIChg)}>
                                                {Number(row.peOIChg).toFixed(0) ?? "--"}
                                                <br />
                                                {getPutOiChgPercentage(row.peOIChg)}
                                            </td>
                                            <td>

                                                {Number(row.peIV).toFixed(2) ?? "--"}
                                            </td>

                                            <td>
                                                {Number(row.peDelta).toFixed(2) ?? "--"}<br />

                                            </td>
                                            <td>

                                                {Number(row.peTheta).toFixed(2) ?? "--"}
                                            </td>
                                            <td>

                                                {Number(row.peGamma).toFixed(2) ?? "--"}
                                            </td>
                                            <td>

                                                {Number(row.peVega).toFixed(2) ?? "--"}
                                            </td>
                                        </tr>

                                        {isSpotBetween && (
                                            <tr className="spot-line">
                                                <td colSpan="3">
                                                    OICHG - {stats.highestCallOiChgStrike} -
                                                    {getCallResistanceOIChgWttAndWtb(stats.secondHighestCallOiChgStrike, stats.secondHighestCallOiChg, stats.highestCallOiChgStrike)}
                                                </td>
                                                <td colSpan="3">
                                                    OI - {stats.highestCallOiStrike} -
                                                    {getCallResistanceOIWttAndWtb(stats.secondHighestCallOiStrike, stats.secondHighestCallOi, stats.highestCallOiStrike)}
                                                </td>
                                                <td colSpan="3">
                                                    RES - {getCallResistanceLogic(stats.highestCallVolStrike, stats.highestCallOiStrike, stats.spotPrice)}  -
                                                    {getCallResistanceWttAndWtb(getCallResistanceLogic(stats.highestCallVolStrike, stats.highestCallOiStrike, stats.spotPrice),
                                                        stats.secondHighestCallVolStrike, stats.secondHighestCallVolume, stats.highestCallVolume)}
                                                </td>
                                                <td colSpan="1">{row.spotPrice}</td>
                                                <td colSpan="3">
                                                    SUP - {getPutSupportLogic(stats.highestPutVolStrike, stats.highestPutOiStrike, stats.spotPrice)}  -
                                                    {getPutSupportWttAndWtb(getPutSupportLogic(stats.highestPutVolStrike, stats.highestPutOiStrike, stats.spotPrice),
                                                        stats.secondHighestPutVolStrike, stats.secondHighestPutVolume, stats.highestPutVolume)}
                                                </td>
                                                <td colSpan="3">
                                                    OI - {stats.highestPutOiStrike} -
                                                    {getPutSupportOIWttAndWtb(stats.secondHighestPutOiStrike, stats.secondHighestPutOi, stats.highestPutOiStrike)}
                                                </td>
                                                <td colSpan="3">
                                                    OICHG - {stats.highestPutOiChgStrike} -
                                                    {getPutSupportOIChgWttAndWtb(stats.secondHighestPutOiChgStrike, stats.secondHighestPutOiChg, stats.highestPutOiChgStrike)}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="17">Waiting for live data...</td>
                            </tr>
                        )}
                    </tbody>
                    {/* <tfoot className="fixed-footer">
            <tr className="footer-left">
              <td colSpan="3">Total-OICHG-SUM ({Number(stats.callTotalOICHGSum).toFixed(0)})</td>
              <td colSpan="3">Total-OI-SUM ({stats.callTotalOISum})</td>
              <td colSpan="3">Total-VOL-SUM ({stats.callTotalVolumeSum})</td>
              <td colSpan="1">PCR ({Number(stats.putTotalOISum - stats.callTotalOISum).toFixed(0)})</td>
              <td colSpan="3">Total-VOL-SUM ({stats.putTotalVolumeSum})</td>
              <td colSpan="3">Total-OI-SUM ({stats.putTotalOISum})</td>
              <td colSpan="3">Total-OICHG-SUM ({Number(stats.putTotalOICHGSum).toFixed(2)})</td>
            </tr >
            
          </tfoot> */}

                </table>
            </div>
        </div>
    );
}
